<template>
  <main-layout
    class="pro-plan-page"
    :page-title="text.planTitle"
    custom-back
    @listen-back="goBack"
  >
    <div slot="main-content" class="main-content">
      <template v-if="entryVisible">
        <div class="plan-header">
          <h1 class="plan-main-title">{{ text.planMainTitle }}</h1>
          <p class="plan-sub-title">{{ text.planSubTitle }}</p>
        </div>

        <div class="plan-cards">
          <plan-card
            v-for="plan in plans"
            :key="plan.id"
            :plan="plan"
            :selected="currentPlan && currentPlan.id === plan.id"
            @select="selectPlan"
          />
        </div>

        <rights-section
          class="rights-block"
          :rights="rights"
          @more="goRightsDetail"
        />

        <div class="pay-bar">
          <div class="pay-bar-left">
            <span class="total"
              >{{ text.totalLabel
              }}<span class="price">￥{{ totalPrice }}</span></span
            >
            <span class="total-desc">{{ priceDesc }}</span>
          </div>
          <el-button
            type="primary"
            class="pay-bar-btn"
            :loading="creating"
            @click="handleUpgrade"
          >
            {{ text.upgradeNow }}
          </el-button>
        </div>
      </template>

      <!-- 资格校验未通过（区域未开放线上支付 → 跳权益页；其余展示后端原因） -->
      <div v-else class="entry-tip">
        <p class="entry-tip-text">{{ reasonDesc }}</p>
        <el-button type="primary" @click="goRightsDetail">{{
          text.viewRightsDetail
        }}</el-button>
      </div>

      <pay-dialogs
        :unpaidVisible.sync="unpaidVisible"
        @go-pay="goOrderList"
        @continue-order="continueOrder"
      />
    </div>
  </main-layout>
</template>

<script>
import MainLayout from "@/layout/MainLayout";
import PlanCard from "./components/PlanCard.vue";
import RightsSection from "./components/RightsSection.vue";
import PayDialogs from "./components/PayDialogs.vue";
import {
  PRO_PLAN_TEXT,
  PRO_PLAN_ERROR,
  PRO_PLAN_ROUTES,
} from "@/consts/proPlan";
import {
  getProPlanList,
  getProPlanRights,
  getCompanyPlanStatus,
  createProOrder,
} from "@/api/request/snkd.proPlan";
import { isAreaClosed } from "@/utils/proPlan";

export default {
  name: "ProPlanIndex",
  components: { MainLayout, PlanCard, RightsSection, PayDialogs },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      plans: [],
      rights: [],
      currentPlan: null,
      unpaidVisible: false,
      creating: false,
      // 购买入口是否展示（受「运营商收费开关 / 区域线上支付」控制）
      entryVisible: true,
      reasonDesc: "",
      // 待支付订单的套餐（「继续下单」时复用）
      pendingPackageId: null,
    };
  },
  computed: {
    totalPrice() {
      return this.currentPlan ? this.currentPlan.price : 0;
    },
    priceDesc() {
      if (!this.currentPlan) return "";
      const sub = this.currentPlan.subName
        ? `（${this.currentPlan.subName}）`
        : "";
      return `${this.text.proPrefix}${this.currentPlan.name}${sub}`;
    },
  },
  async created() {
    await this.getList();
  },
  methods: {
    async getList() {
      const [planRes, rightsRes, statusRes] = await Promise.all([
        getProPlanList({ companyId: this.$route.query.companyId }),
        getProPlanRights(),
        getCompanyPlanStatus({ companyId: this.$route.query.companyId }),
      ]);
      // 套餐顺序由后端保证，前端不排序，默认选中第一个
      const plans = planRes.obj || [];
      this.plans = plans;
      this.currentPlan = plans[0] || null;
      this.rights = rightsRes.obj || [];

      // 购买资格：41003 收费开关关闭 → 不展示入口；区域未开放 → 跳权益页；41005 无农场 → 展示原因
      // 先复位，保证重复调用（同路由换企业等）时状态不被上一次结果污染
      this.entryVisible = true;
      const status = statusRes.obj || {};
      if (statusRes.respCode !== 0 || status.qualified === false) {
        this.entryVisible = false;
        this.reasonDesc = status.reasonDesc || "";
        // 区域未开放判定见 isAreaClosed（reasonCode 41004 优先，缺失时退回 qualified=false）
        if (isAreaClosed(status)) {
          this.goRights();
        }
      }
    },
    selectPlan(plan) {
      this.currentPlan = plan;
    },
    async handleUpgrade() {
      if (!this.currentPlan) return;
      this.pendingPackageId = this.currentPlan.packageId;
      await this.createOrder(false);
    },
    // 「继续下单」：带 continueFlag 重试（后端会关闭已有订单并新建）
    continueOrder() {
      this.unpaidVisible = false;
      const packageId = this.pendingPackageId || this.currentPlan.packageId;
      this.createOrder(true, packageId);
    },
    /**
     * 创建订单并跳转订单确认页
     * @param {boolean} continueFlag 是否有待支付订单时继续下单
     */
    async createOrder(continueFlag, packageId) {
      this.creating = true;
      try {
        const res = await createProOrder({
          companyId: this.$route.query.companyId,
          packageId: packageId || this.currentPlan.packageId,
          continueFlag: !!continueFlag,
        });
        // 42001：存在待支付订单 → 弹「继续下单 / 前往支付」
        if (res.respCode === PRO_PLAN_ERROR.HAS_UNPAID_ORDER) {
          this.pendingPackageId = packageId || this.currentPlan.packageId;
          this.unpaidVisible = true;
          return;
        }
        // 42003：该套餐订单已支付 → 直接进订单列表
        if (res.respCode === PRO_PLAN_ERROR.ORDER_PAID) {
          this.$message.success(res.respDesc || this.text.paySuccess);
          this.goOrderList();
          return;
        }
        if (res.respCode !== 0) {
          this.$message.error(res.respDesc || this.text.payFailed);
          return;
        }
        this.goConfirm(res.obj || {});
      } catch (e) {
        console.log(e);
      } finally {
        this.creating = false;
      }
    },
    goConfirm(order) {
      const plan = this.currentPlan;
      this.$router.push({
        path: "/proPlan/orderConfirm",
        query: {
          companyId: this.$route.query.companyId,
          orderId: order.orderId,
          // 二维码链接由 add 接口下发，直接带给确认页渲染
          codeUrl: order.codeUrl,
          // 支付有效期截止时间（字符串），驱动倒计时
          expireTime: order.expireTime,
          orderNo: order.orderNo,
          packageId: plan ? plan.packageId : order.packageId,
          planName: plan ? plan.name : order.packageName,
          months: plan ? plan.months : order.months,
          price: plan ? plan.price : order.price,
        },
      });
    },
    /**
     * 【前往支付】：按需求跳专业版订单列表页，由用户在列表中选择订单支付
     * （不走 pending 直达旧单，禅道 Bug#23431）。
     */
    goOrderList() {
      // 弹框挂在当前页面内，跳转前先收起，避免返回时残留
      this.unpaidVisible = false;
      this.$router.push({
        path: "/proPlan/orderList",
        query: { companyId: this.$route.query.companyId },
      });
    },
    /**
     * 「查看权益详情」→ 权益详情页（9 大权益逐条说明，PC/H5 同页）
     * 注意与 goRights 区分：后者是「区域未开通线上支付」时的兜底去向（联系客服页）
     */
    goRightsDetail() {
      this.$router.push({
        path: PRO_PLAN_ROUTES.rightsDetail,
        query: { companyId: this.$route.query.companyId },
      });
    },
    goRights() {
      this.$router.replace({
        path: "/proPlan/rights",
        query: { companyId: this.$route.query.companyId },
      });
    },
    goBack() {
      window.history.length > 1 ? this.$router.back() : window.history.back();
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  min-height: 100%;
  // 设计稿（1881:993）：头部高 80，主标题绝对 y=110 → 头部到标题间距 30px。
  // 实现的 el-header 已占 0~80，故此处取 30px（原先 10px 偏小，禅道 Bug#23428-1）
  padding: 30px 0 130px;
  box-sizing: border-box;
  background: #f7f9fb;
  position: relative;
}
.plan-header {
  text-align: center;
  .plan-main-title {
    margin: 0;
    font-size: 40px;
    font-weight: 400;
    color: #434343;
    line-height: 1.2;
  }
  .plan-sub-title {
    margin: 15px 0 0;
    font-size: 16px;
    color: #888;
    line-height: 24px;
  }
}
.plan-cards {
  width: 1100px;
  margin: 30px auto 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: stretch;
  gap: 20px;
}
.rights-block {
  margin-top: 36px;
}
// 资格未通过时的提示
.entry-tip {
  padding-top: 160px;
  text-align: center;
  .entry-tip-text {
    margin: 0 0 20px;
    font-size: 16px;
    color: #62717e;
  }
}
.pay-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 96px;
  padding: 0 20px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 50px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  z-index: 10;
  .pay-bar-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    .total {
      font-size: 16px;
      font-weight: 700;
      color: #666;
      .price {
        font-size: 24px;
        font-weight: 700;
        color: #ff4f4f;
      }
    }
    .total-desc {
      font-size: 12px;
      color: #62717e;
      line-height: 24px;
    }
  }
  .pay-bar-btn {
    width: 101px;
    height: 38px;
    padding: 0;
    border-radius: 10px;
    background: #0fa87b;
    border-color: #0fa87b;
    font-size: 16px;
  }
}
</style>
